import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';
import { useFocusTrap, useScrollLock } from './overlay';

type LayerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Sticky footer, usually right-aligned buttons. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Disable closing via Esc / backdrop (e.g. while a request is in flight). */
  dismissible?: boolean;
};

/**
 * Esc closes the top-most layer only: focus is trapped inside each layer, so the keydown originates in the
 * layer that is on top, and nested popovers stop propagation of their own Esc.
 */
function onLayerKeyDown(event: KeyboardEvent, dismissible: boolean, onClose: () => void) {
  if (event.key !== 'Escape' || !dismissible) return;
  event.stopPropagation();
  onClose();
}

export type DialogProps = LayerProps & { size?: 'sm' | 'md' | 'lg' };

const DIALOG_SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

/** Centered modal. Use sparingly — prefer SidePanel for forms and inline editing for small changes. */
export function Dialog({ open, onClose, title, description, footer, children, dismissible = true, size = 'md' }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useFocusTrap(panelRef, open);
  useScrollLock(open);
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:pt-[12vh]">
      <div
        aria-hidden
        className="fixed inset-0 animate-fade-in bg-slate-900/40 backdrop-blur-[1px] dark:bg-black/60"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={(e) => onLayerKeyDown(e, dismissible, onClose)}
        className={clsx(
          'relative w-full animate-pop-in rounded-xl border border-slate-200 bg-surface shadow-panel focus:outline-none',
          DIALOG_SIZES[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-2">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[15px] font-semibold text-slate-900">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-[13px] text-slate-500">
                {description}
              </p>
            )}
          </div>
          {dismissible && <IconButton icon={X} label="Close" size="sm" onClick={onClose} data-focus-skip className="-mr-1.5" />}
        </div>
        {children && <div className="px-5 py-3 text-[13px] text-slate-700">{children}</div>}
        {footer && (
          <div className="flex items-center justify-end gap-2 rounded-b-xl border-t border-slate-100 bg-slate-50/70 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export type SidePanelProps = LayerProps & { width?: 'md' | 'lg' | 'xl' };

const PANEL_WIDTHS = { md: 'max-w-md', lg: 'max-w-xl', xl: 'max-w-3xl' } as const;

/** Right slide-over drawer for create/edit forms and record previews. */
export function SidePanel({ open, onClose, title, description, footer, children, dismissible = true, width = 'lg' }: SidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panelRef, open);
  useScrollLock(open);
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40">
      <div aria-hidden className="absolute inset-0 animate-fade-in bg-slate-900/30 dark:bg-black/50" onClick={dismissible ? onClose : undefined} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={(e) => onLayerKeyDown(e, dismissible, onClose)}
        className={clsx(
          'absolute inset-y-0 right-0 flex w-full animate-slide-in-right flex-col border-l border-slate-200 bg-surface shadow-panel focus:outline-none',
          PANEL_WIDTHS[width],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-3.5">
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-[15px] font-semibold text-slate-900">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-[13px] text-slate-500">{description}</p>}
          </div>
          {dismissible && <IconButton icon={X} label="Close panel" size="sm" onClick={onClose} data-focus-skip />}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-5 py-3">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
